import React from 'react';
import { Card, Group, Stack, Text, Badge, Avatar, Tooltip, Button, Divider } from '@mantine/core';
import { IconEdit } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ReviewCycle } from '../../types';
import { formatDateShort } from '../../utils/date';

interface CycleInfoCardProps {
  cycle: ReviewCycle;
}

export const CycleInfoCard: React.FC<CycleInfoCardProps> = ({ cycle }) => {
  const { t } = useTranslation();

  const anonymitySummary = [
    cycle.anonymitySettings.managerAnonymous
      ? t('review360.managerHidden')
      : t('review360.managerVisible'),
    t('review360.peersMin', { count: cycle.anonymitySettings.peerMinThreshold }),
    t('review360.subordinatesMin', { count: cycle.anonymitySettings.subordinateMinThreshold }),
  ].join(' / ');

  const templateName = cycle.questionTemplate && cycle.questionTemplate.length > 0
    ? `${cycle.questionTemplate.length} ${t('review360.sections').toLowerCase()}`
    : '—';

  return (
    <Card withBorder radius="md" p="lg">
      <Stack gap="md">
        {/* Title + type badge */}
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Text fw={600} size="lg">
              {cycle.title}
            </Text>
            {cycle.description && (
              <Text c="dimmed" size="sm">
                {cycle.description}
              </Text>
            )}
          </Stack>
          <Badge color={cycle.type === '360' ? 'teal' : 'blue'} variant="light" size="lg">
            {cycle.type === '360' ? t('review360.type360') : t('review360.type180')}
          </Badge>
        </Group>

        <Divider />

        {/* Details grid */}
        <Group gap="xl" wrap="wrap">
          {/* Template */}
          <Stack gap={2}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              {t('review360.templateLabel')}
            </Text>
            <Text size="sm">{templateName}</Text>
          </Stack>

          {/* Nomination deadline */}
          <Stack gap={2}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              {t('review360.deadlineNomination')}
            </Text>
            <Text size="sm">{formatDateShort(cycle.deadlines?.nomination)}</Text>
          </Stack>

          {/* Response deadline */}
          <Stack gap={2}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              {t('review360.deadlineResponse')}
            </Text>
            <Text size="sm">{formatDateShort(cycle.deadlines?.response)}</Text>
          </Stack>

          {/* Anonymity */}
          <Stack gap={2}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              {t('review360.anonymitySummary')}
            </Text>
            <Text size="sm">{anonymitySummary}</Text>
          </Stack>
        </Group>

        {/* Subjects */}
        <Stack gap={4}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            {t('review360.subjects')}
          </Text>
          <Avatar.Group>
            {cycle.subjects?.slice(0, 8).map((subject) => (
              <Tooltip key={subject._id} label={subject.name} withArrow>
                <Avatar src={subject.avatar} radius="xl" size="sm">
                  {subject.name?.charAt(0)}
                </Avatar>
              </Tooltip>
            ))}
            {(cycle.subjects?.length ?? 0) > 8 && (
              <Avatar radius="xl" size="sm">
                +{(cycle.subjects?.length ?? 0) - 8}
              </Avatar>
            )}
          </Avatar.Group>
        </Stack>

        {/* Edit button — only in draft */}
        {cycle.status === 'draft' && (
          <>
            <Divider />
            <Group justify="flex-end">
              <Button variant="light" leftSection={<IconEdit size={16} />}>
                {t('review360.editCycle')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Card>
  );
};
